import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, ChevronLeft, Trophy, Medal, Award } from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import ReportFilters, { presetToRange } from './ReportFilters';

const formatINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });


export default function ReferrerLeaderboardReport() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('converted');
  const [preset, setPreset] = useState('last_90');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { from, to } = presetToRange(preset, customFrom, customTo);
        const q = new URLSearchParams({ order_by: orderBy, limit: 25 });
        if (from) q.set('from', from);
        if (to)   q.set('to', to);
        const resp = await apiFetch(`engage/analytics/referrer-leaderboard/?${q.toString()}`);
        const results = resp?.data?.data || resp?.results || resp?.data?.results || [];
        setRows(Array.isArray(results) ? results : []);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, preset, customFrom, customTo, reloadTick]);

  return (
    <div className="p-4 max-w-[1200px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/dashboard')}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center text-white shadow-sm">
            <Trophy className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Referrer Leaderboard</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Top-performing referring doctors (last 90 days)</p>
          </div>
        </div>
      </div>

      <ReportFilters
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        onReload={() => setReloadTick(t => t + 1)}
        extras={[{
          label: 'Rank by', value: orderBy, onChange: setOrderBy,
          options: [
            { value: 'converted', label: 'Rank by conversions' },
            { value: 'referrals', label: 'Rank by referrals' },
            { value: 'incentive_paid_total', label: 'Rank by payouts' },
          ],
        }]}
      />

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Rank</th>
              <th className="px-4 py-2 text-left font-medium">Doctor</th>
              <th className="px-4 py-2 text-right font-medium">Referrals</th>
              <th className="px-4 py-2 text-right font-medium">Converted</th>
              <th className="px-4 py-2 text-right font-medium">Conv. %</th>
              <th className="px-4 py-2 text-right font-medium">Paid</th>
              <th className="px-4 py-2 text-right font-medium">Owed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                No referrals in the last 90 days yet.
              </td></tr>
            ) : rows.map((r, i) => {
              const referrals = r.referrals_total || 0;
              const converted = r.referrals_converted || 0;
              const convPct = referrals ? Math.round(converted / referrals * 100) : 0;
              return (
                <tr key={r.doctor_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-2.5">
                    {i === 0 ? <span className="inline-flex items-center gap-1 text-amber-500 font-bold"><Trophy className="w-4 h-4" /> #1</span>
                    : i === 1 ? <span className="inline-flex items-center gap-1 text-slate-500 font-bold"><Medal className="w-4 h-4" /> #2</span>
                    : i === 2 ? <span className="inline-flex items-center gap-1 text-orange-500 font-bold"><Award className="w-4 h-4" /> #3</span>
                    : <span className="text-gray-500">#{i + 1}</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <Link to={`/engage/referring-doctors/${r.doctor_id}`} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                      {r.doctor_name}
                    </Link>
                    {r.specialty && <div className="text-[11px] text-gray-500 dark:text-gray-400">{r.specialty}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium text-gray-900 dark:text-white">{referrals}</td>
                  <td className="px-4 py-2.5 text-right text-green-600 dark:text-green-400 font-medium">{converted}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={cn(
                      'inline-block px-2 py-0.5 rounded-full text-[10px] font-medium',
                      convPct >= 40 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                      convPct >= 20 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    )}>
                      {convPct}%
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-green-600 dark:text-green-400">₹ {formatINR(r.incentive_paid_total)}</td>
                  <td className="px-4 py-2.5 text-right text-amber-600 dark:text-amber-400 font-medium">
                    ₹ {formatINR(r.incentive_unpaid_total)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
